import { Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { eur } from "@/lib/api";

const LAWYER_IMG = "/img/avv-depasquale.jpg";
const OFFICE_IMG = "/img/salone.jpg";

const Eyebrow = ({ children }) => <p className="text-xs uppercase tracking-[0.3em] text-gold-dark font-semibold">{children}</p>;

export const TrustBar = ({ s }) => (
  <section className="bg-white border-b border-slate-200" data-testid="trust-bar">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-wrap gap-x-10 gap-y-3 text-xs uppercase tracking-widest text-slate-500">
      <span>{s?.avvocato} · {s?.ordine}{s?.iscrizione ? ` · ${s.iscrizione}` : ""}</span><span>Informativa GDPR art. 13</span><span>Verifica firme PAdES / CAdES</span><span>L. 266/2005 · DPR 116/2007</span>
    </div>
  </section>
);

const STEPS = [
  ["Verifica gratuita", "Compili il form: confrontiamo i tuoi dati con l'elenco ufficiale dei rapporti dormienti."],
  ["Mandato", "Se c'è una posizione, firmi il mandato online (firma digitale) o su carta."],
  ["Documenti e istanza", "Raccogliamo i documenti, completiamo l'adeguata verifica e presentiamo l'istanza a Consap."],
  ["Incasso e saldo", "Consap liquida le somme: tratteniamo il compenso concordato e ti bonifichiamo il saldo con rendiconto."],
];

export const HowItWorks = () => (
  <section id="come-funziona" className="py-20 sm:py-28 max-w-7xl mx-auto px-5 sm:px-8" data-testid="how-it-works-section">
    <Eyebrow>Come funziona</Eyebrow>
    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy mt-3 max-w-2xl">Quattro passaggi, un solo referente</h2>
    <ol className="mt-14 grid md:grid-cols-4 gap-10 md:gap-6">
      {STEPS.map(([t, s], i) => (
        <li key={t} className="relative md:pr-6 md:border-r last:border-r-0 border-slate-200">
          <span className="font-serif text-6xl text-gold/70 leading-none">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="font-serif text-2xl text-navy mt-3">{t}</h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s}</p>
        </li>
      ))}
    </ol>
  </section>
);

export const Pricing = ({ s }) => (
  <section id="tariffe" className="bg-navy text-white py-20 sm:py-28 relative grain" data-testid="pricing-section">
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Tariffe trasparenti</p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3">Paghi solo se recuperi</h2>
        <p className="text-slate-300 mt-5 leading-relaxed max-w-lg">Nessun costo di verifica, nessun anticipo. Il compenso viene trattenuto dalle somme effettivamente liquidate, con rendiconto scritto.</p>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-md">
        <p className="text-sm text-slate-400">Success fee</p>
        <p className="font-serif text-7xl text-gold mt-1" data-testid="pricing-success-fee">{s?.success_fee_pct ?? 20}%</p>
        <p className="text-sm text-slate-400">delle somme incassate, oltre accessori di legge se dovuti</p>
        <ul className="mt-8 space-y-3 text-sm">
          {["Verifica gratuita e senza impegno", "Istanza e rapporti con Consap inclusi", "Saldo bonificato entro 15 giorni lavorativi",
            `Penale di ${eur(s?.penale_bypass ?? 300)} solo in caso di by-pass del mandato`, "Revoca sempre possibile (compenso ministeriale per l'attività svolta)"].map((t) => (
            <li key={t} className="flex gap-3"><Check className="h-4 w-4 text-gold mt-0.5 shrink-0" />{t}</li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

export const About = ({ s }) => (
  <section id="chi-siamo" className="py-20 sm:py-28 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.2fr] gap-14 items-center" data-testid="about-section">
    <figure className="relative pb-16 sm:pb-10">
      <img src={OFFICE_IMG} alt="Sala riunioni dello studio Legalia@ a Manduria" data-testid="about-office-photo" className="rounded-xl object-cover h-72 sm:h-96 w-full" />
      <figcaption className="absolute -bottom-2 sm:bottom-0 right-4 sm:-right-4 bg-white rounded-xl shadow-xl border border-slate-200 p-3 flex items-center gap-3">
        <img src={LAWYER_IMG} alt={s?.avvocato || "Avvocato incaricato"} data-testid="about-lawyer-photo" className="h-20 w-20 sm:h-24 sm:w-24 rounded-lg object-cover" />
        <span className="pr-2">
          <span className="block font-serif text-lg text-navy leading-tight">{s?.avvocato}</span>
          <span className="block text-xs text-slate-500 mt-0.5">{s?.ordine}</span>
          {s?.iscrizione && <span className="block text-xs text-gold-dark font-semibold">{s.iscrizione}</span>}
        </span>
      </figcaption>
    </figure>
    <div>
      <Eyebrow>Chi siamo</Eyebrow>
      <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy mt-3">Una società tra avvocati, non una piattaforma anonima</h2>
      <p className="text-slate-600 mt-5 leading-relaxed">{s?.studio_nome} segue ogni pratica di recupero con un professionista dedicato: l'{s?.avvocato}, iscritto all'{s?.ordine}{s?.iscrizione ? ` (${s.iscrizione})` : ""}. Applichiamo l'adeguata verifica antiriciclaggio e custodiamo i tuoi documenti con misure conformi al GDPR.</p>
      <dl className="mt-8 grid sm:grid-cols-2 gap-4 text-sm">
        {[["Sede", s?.indirizzo], ["PEC", s?.pec], ["Partita IVA", s?.piva], ["Iscrizione", [s?.ordine, s?.iscrizione].filter(Boolean).join(" · ")], ["Telefono", s?.telefono]].filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="border-l-2 border-gold pl-4"><dt className="text-xs uppercase tracking-widest text-slate-500">{k}</dt><dd className="text-navy font-medium mt-1 break-words">{v}</dd></div>
        ))}
      </dl>
    </div>
  </section>
);

const FAQS = [
  ["Cos'è un deposito dormiente?", "È un rapporto bancario, postale o assicurativo (conto, libretto, dossier titoli, polizza) su cui non sono state effettuate operazioni per oltre 10 anni. Le somme vengono trasferite al Fondo gestito da Consap."],
  ["Chi può chiedere il rimborso?", "Il titolare del rapporto oppure i suoi eredi, entro il termine di prescrizione decennale dalla devoluzione al Fondo."],
  ["Perché non mostrate l'importo dopo la verifica?", "Per tutelare la riservatezza: importi e istituti vengono comunicati solo al titolare o agli eredi legittimati, dopo il conferimento del mandato e l'identificazione."],
  ["Quanto costa?", "La verifica è gratuita. In caso di recupero, lo studio trattiene la success fee concordata sulle somme incassate. Se non recuperi nulla, non paghi nulla."],
  ["Come firmo il mandato?", "Puoi caricare il PDF firmato con la tua firma digitale (PAdES o CAdES .p7m) oppure stamparlo, firmarlo a mano e inviarci la scansione."],
  ["Quanto tempo serve?", "Dipende dai tempi di istruttoria di Consap: indicativamente da 3 a 9 mesi dalla presentazione dell'istanza completa."],
];

export const Faq = () => (
  <section id="faq" className="bg-white border-y border-slate-200 py-20 sm:py-28" data-testid="faq-section">
    <div className="max-w-3xl mx-auto px-5 sm:px-8">
      <Eyebrow>Domande frequenti</Eyebrow>
      <h2 className="font-serif text-3xl sm:text-4xl text-navy mt-3">Risposte chiare</h2>
      <Accordion type="single" collapsible className="mt-10">
        {FAQS.map(([q, a], i) => (
          <AccordionItem key={q} value={`f${i}`}>
            <AccordionTrigger data-testid={`faq-item-${i}`} className="text-left font-serif text-xl text-navy hover:no-underline">{q}</AccordionTrigger>
            <AccordionContent className="text-slate-600 leading-relaxed">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);
